import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Paper,
  ListItem,
  Box,
  Modal,
  Card,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { Button } from "@mui/material";
import crudsService from "services/cruds-service";

const ColumnSetting = ({ open, onClose, columns, setColumns }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tempColumn, setTempColumn] = useState({
    showColumn: columns.filter((column) => column.is_visible),
    hideColumn: columns.filter((column) => !column.is_visible),
  });

  useEffect(() => {
    if (columns.length) {
      setTempColumn({
        showColumn: columns.filter((column) => column.is_visible),
        hideColumn: columns.filter((column) => !column.is_visible),
      });
    }
  }, [columns]);

  const onSave = async () => {
    const showColumns = tempColumn.showColumn.map((elem, index) => ({
      ...elem,
      position: index + 1,
      is_visible: true,
    }));
    const hideColumns = tempColumn.hideColumn.map((elem, index) => ({
      ...elem,
      position: index + 1,
      is_visible: false,
    }));

    const data = {
      code: "CAMP_DASH",
      columns: [...showColumns, ...hideColumns],
    };

    try {
      await crudsService.setSetting(data);
      setColumns([...showColumns, ...hideColumns]);
      onClose();
    } catch (err) {
      console.error("Error saving settings:", err);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceItems = Array.from(tempColumn[source.droppableId]);
    const destItems =
      source.droppableId === destination.droppableId
        ? sourceItems
        : Array.from(tempColumn[destination.droppableId]);

    const [movedItem] = sourceItems.splice(source.index, 1);
    destItems.splice(destination.index, 0, {
      ...movedItem,
      position: destination.index,
    });

    const updatedColumns = {
      ...tempColumn,
      [source.droppableId]: sourceItems,
      ...(source.droppableId !== destination.droppableId && {
        [destination.droppableId]: destItems,
      }),
    };

    setTempColumn(updatedColumns);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="column-settings-modal"
      aria-describedby="modal-to-manage-column-visibility"
    >
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        <Card
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: { xs: "90%", sm: "40%" },
            maxWidth: "700px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            boxShadow: 24,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: 2,
            }}
          >
            <DragDropContext onDragEnd={onDragEnd}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 2,
                }}
              >
                <DroppableArea
                  title="Show Column"
                  droppableId="showColumn"
                  items={tempColumn.showColumn}
                  darkMode={darkMode}
                />
                <DroppableArea
                  title="Hide Column"
                  droppableId="hideColumn"
                  items={tempColumn.hideColumn}
                  darkMode={darkMode}
                />
              </Box>
            </DragDropContext>
          </Box>
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: "1px solid #ccc",
            }}
          >
            <Button variant="contained" onClick={onSave}>
              Save
            </Button>
          </Box>
        </Card>
      </Box>
    </Modal>
  );
};

const DroppableArea = ({ title, droppableId, items, darkMode }) => {
  return (
    <Droppable droppableId={droppableId}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            flex: 1,
            // border: "1px solid #ccc",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: "60vh",
            maxHeight: "60vh",
          }}
        >
          <MDTypography
            textAlign="center"
            sx={{
              py: 1,
              backgroundColor: darkMode ? "#1a2035" : "#fff",
            }}
          >
            {title}
          </MDTypography>
          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1 }}>
            {items.map((item, index) => (
              <Draggable
                key={item.column_name}
                draggableId={item.column_name}
                index={index}
              >
                {(provided, snapshot) => (
                  <Paper
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{
                      p: 1,
                      my: 1,
                      backgroundColor: snapshot.isDragging
                        ? "#e6f7ff"
                        : "lightgray",
                    }}
                  >
                    <ListItem disablePadding>{item.column_name}</ListItem>
                  </Paper>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        </Box>
      )}
    </Droppable>
  );
};

export default ColumnSetting;
